/**
 * HoverLegendLineChart
 *
 * An apex line chart where hovering a legend entry labels that line's value at
 * every point, right on the line (apex itself dims the other lines) — the same
 * behaviour as the Trends tab's ServicelineTrendsChart, packaged for the
 * Relationship tabs.
 *
 * Apex draws the legend, so the hovered entry is found by its class; its `rel`
 * attribute is the 1-based series index. Point positions come from apex's own
 * layout (w.globals), so the labels sit exactly on the drawn points.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';

const styles = (theme) => ({
  chartWrap: {
    position: 'relative',
  },
  // value labels drawn over the chart for the hovered legend entry; they never
  // intercept the mouse, so the chart and legend keep working underneath
  pointDot: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.background.paper}`,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  },
  pointLabel: {
    position: 'absolute',
    padding: '2px 8px',
    borderRadius: 12,
    border: '1px solid',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '18px',
    whiteSpace: 'nowrap',
    boxShadow: theme.shadows[1],
    pointerEvents: 'none',
    // sits centred just above its point
    transform: 'translate(-50%, calc(-100% - 8px))',
  },
});

function HoverLegendLineChart({ classes, options, series, height }) {
  const chartRef = useRef(null);
  const chartWrapRef = useRef(null);
  const [legendHover, setLegendHover] = useState(null);

  // Memoized on the caller's options so hovering (a state change here) doesn't
  // hand apex a new options object and make it rebuild the legend under the
  // mouse. The caller's own chart events are kept.
  const chartOptions = useMemo(
    () => ({
      ...options,
      chart: {
        ...options.chart,
        events: {
          ...(options.chart && options.chart.events),
          // keep the apex instance: the hover reads point positions off it
          mounted: (chart) => {
            chartRef.current = chart;
          },
          // also fires after a legend click toggles a line: the y-axis
          // rescales, so any labels on screen are now in the wrong place
          updated: (chart) => {
            chartRef.current = chart;
            setLegendHover(null);
          },
        },
      },
    }),
    [options],
  );

  useEffect(() => {
    setLegendHover(null);
  }, [series]);

  // mousemove rather than mouseover, so the labels come back without leaving
  // the entry after a click toggles its line back on
  const onMouseMove = (e) => {
    const item = e.target.closest('.apexcharts-legend-series');
    if (!item) return;
    const index = parseInt(item.getAttribute('rel'), 10) - 1;
    if (legendHover && legendHover.index === index) return;
    const chart = chartRef.current;
    const line = series[index];
    const globals = chart && chart.w && chart.w.globals;
    if (!line || !globals || !chartWrapRef.current || !chart.el) return;
    // a line toggled off in the legend has nothing to label
    if ((globals.collapsedSeriesIndices || []).includes(index)) return;
    const xs = globals.seriesXvalues[index] || [];
    const ys = globals.seriesYvalues[index] || [];
    // apex positions are relative to the plot area; shift them to our wrapper
    const wrapRect = chartWrapRef.current.getBoundingClientRect();
    const chartRect = chart.el.getBoundingClientRect();
    const originX = chartRect.left - wrapRect.left + globals.translateX;
    const originY = chartRect.top - wrapRect.top + globals.translateY;
    const points = [];
    line.data.forEach((val, i) => {
      if (val === null || val === undefined) return;
      if (!Number.isFinite(xs[i]) || !Number.isFinite(ys[i])) return;
      points.push({
        key: i,
        left: originX + xs[i],
        top: originY + ys[i],
        text: val.toLocaleString(),
      });
    });
    setLegendHover({ index, color: globals.colors[index], points });
  };
  const onMouseOut = (e) => {
    const item = e.target.closest('.apexcharts-legend-series');
    if (item && !item.contains(e.relatedTarget)) {
      setLegendHover(null);
    }
  };

  return (
    <div
      ref={chartWrapRef}
      className={classes.chartWrap}
      onMouseMove={onMouseMove}
      onMouseOut={onMouseOut}
      onClick={() => setLegendHover(null)}
    >
      <Chart type='line' height={height} series={series} options={chartOptions} />
      {legendHover &&
        legendHover.points.map((point) => (
          <React.Fragment key={point.key}>
            <span
              className={classes.pointDot}
              style={{
                left: point.left,
                top: point.top,
                backgroundColor: legendHover.color,
              }}
            />
            <span
              className={classes.pointLabel}
              style={{
                left: point.left,
                top: point.top,
                borderColor: legendHover.color,
              }}
            >
              {point.text}
            </span>
          </React.Fragment>
        ))}
    </div>
  );
}

HoverLegendLineChart.propTypes = {
  classes: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  series: PropTypes.array.isRequired,
  height: PropTypes.number,
};

HoverLegendLineChart.defaultProps = {
  height: 340,
};

const Styled = withStyles(styles)(HoverLegendLineChart);
export { Styled as HoverLegendLineChart };
